import React, { memo } from "react";
import moment from "moment";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { AmountInt, DashboardFlowKeyStatistics } from "@generated/v2";
import { DateFormats } from "app/constants/dateConst";
import styles from "./KeyStatistics.module.scss";
import Icon from "app/components/Icon/Icon";
import { CenteredSpan } from "app/components/Common/CenteredSpan";
import { Chip, ChipProps } from "app/components/Common/Chip";

const RenderDelta = memo(
  ({
    increase,
    reverseColors,
    neutral,
  }: {
    increase: number;
    reverseColors: boolean;
    neutral: boolean;
  }) => {
    const { t } = useTranslation();

    const amount = parseFloat(increase.toFixed(1));

    const isNegative = increase < 0;

    const negativeVariant = !reverseColors ? "negative" : "positive";
    const positiveVariant = !reverseColors ? "positive" : "negative";

    const variant: ChipProps["variant"] = neutral
      ? "neutral"
      : isNegative
      ? negativeVariant
      : positiveVariant;

    return (
      <Chip
        variant={variant}
        text={t(
          isNegative
            ? "dashboard.statisticsFlowDecrease"
            : "dashboard.statisticsFlowIncrease",
          { amount }
        )}
        icon={<Icon type={isNegative ? "caret-down" : "caret-up"} />}
      />
    );
  }
);

type KeyStatisticKey = keyof Pick<
  DashboardFlowKeyStatistics,
  | "total"
  | "negative"
  | "positive"
  | "inconclusive"
  | "inconclusiveRatio"
  | "positiveRatio"
>;
const defaultAmount: AmountInt = {
  amount: 0,
  increase: 0,
};

interface KeyStatisticCardProps {
  data: DashboardFlowKeyStatistics | undefined;
  statistic: KeyStatisticKey;
  description?: string;
  info?: string;
  reverseColors?: boolean;
  nuetralColor?: boolean;
  icon: React.ReactNode;
}

const getDateWithoutTimezone = (value: string) => {
  return moment(value.split("T")[0]);
};

export const KeyStatisticCard = memo(
  ({
    data,
    info,
    statistic,
    description,
    reverseColors = false,
    nuetralColor = false,
    icon,
  }: KeyStatisticCardProps) => {
    const keyStatistic = !!data ? data[statistic] : defaultAmount;
    const isPercentageAmount =
      statistic === "positiveRatio" || statistic === "inconclusiveRatio";

    const singleDecimalAmount =
      Math.floor((keyStatistic.amount ?? 0) * 10) / 10;

    const amount = isPercentageAmount
      ? `${singleDecimalAmount.toFixed(1)}%`
      : keyStatistic.amount;
    const increase = keyStatistic.increase;

    return (
      <div className={styles.total}>
        <div className={styles.totalHeader}>
          {icon}
          <p className={styles.description}>
            <CenteredSpan>{description.toUpperCase()}</CenteredSpan>
          </p>
          {!!info && (
            <Tooltip title={info} arrowPointAtCenter placement="topRight">
              <Icon type="info-circle" className={styles.info} />
            </Tooltip>
          )}
        </div>
        {amount != null && (
          <p className={styles.amount}>
            <CenteredSpan>{amount}</CenteredSpan>
          </p>
        )}
        <div className={styles.totalFooter}>
          {increase != null && (
            <RenderDelta
              increase={increase}
              reverseColors={reverseColors}
              neutral={nuetralColor}
            />
          )}
          {!!data?.comparedPeriodStart && !!data?.comparedPeriodEnd && (
            <>
              <p className={styles.period}>Compared to</p>
              <p className={styles.period}>
                {getDateWithoutTimezone(data.comparedPeriodStart).format(
                  DateFormats.DATE_DMY_NO_HYPHEN
                )}{" "}
                -{" "}
                {getDateWithoutTimezone(data.comparedPeriodEnd).format(
                  DateFormats.DATE_DMY_NO_HYPHEN
                )}
              </p>
            </>
          )}
        </div>
      </div>
    );
  }
);
